import React, { useCallback, useEffect, useState } from 'react';
import styles from './PedidosEntrega.module.css';
import HeaderPageEquipe from '../../operador/components/HeaderPageEquipe';
import FooterOpcoes from '../../cliente/components/FooterOpcoes';
import { fetchWithAuth } from '../../../shared/utils/auth';
import { sseService } from '../../../shared/services/sse';
import { numeroExibicaoPedido } from '../../../shared/utils/pedido';
import { MapPin, Phone, Package, Clock, X, Navigation } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

function linkGoogleMaps(endereco) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco || '')}`;
}

function linkTelefone(telefone) {
  return `tel:${(telefone || '').replace(/\D/g, '')}`;
}

// Pedido de WhatsApp não tem Cliente (login do app) — nome/telefone vêm de
// whatsappCustomer nesse caso (ver backend/controllers/entregador.controller.js).
function nomeCliente(pedido) {
  return pedido.cliente?.nome || pedido.whatsappCustomer?.nome || 'Cliente';
}

function telefoneCliente(pedido) {
  return pedido.cliente?.telefone || pedido.whatsappCustomer?.phone || null;
}

const PedidosEntrega = () => {
  const nomeEstabelecimento = localStorage.getItem('nomeEstabelecimento');
  const idEstabelecimento = localStorage.getItem('estabelecimento_id');

  const [aba, setAba] = useState('disponiveis');
  const [disponiveis, setDisponiveis] = useState([]);
  const [minhas, setMinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [pedidoAberto, setPedidoAberto] = useState(null);
  const [codigoDigitado, setCodigoDigitado] = useState('');
  const [erroAcao, setErroAcao] = useState('');
  const [processando, setProcessando] = useState(false);

  const buscarPedidos = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/entregador/pedidos`);
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        console.error('Erro ao buscar pedidos de entrega:', data);
        return;
      }
      setDisponiveis(data.disponiveis || []);
      setMinhas(data.minhas || []);
    } catch (error) {
      console.error('Erro ao buscar pedidos de entrega:', error);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    buscarPedidos();
  }, [buscarPedidos]);

  // SSE (novo pedido pronto, outro entregador aceitou/entregou) + polling de
  // segurança, mesmo padrão do PedidosContext.
  useEffect(() => {
    if (idEstabelecimento) {
      sseService.connect(idEstabelecimento);
    }

    const handleAtualizacao = () => buscarPedidos();
    sseService.on('novo_pedido', handleAtualizacao);
    sseService.on('pedido_atualizado', handleAtualizacao);

    const intervalo = setInterval(buscarPedidos, 15000);

    return () => {
      sseService.off('novo_pedido', handleAtualizacao);
      sseService.off('pedido_atualizado', handleAtualizacao);
      clearInterval(intervalo);
    };
  }, [idEstabelecimento, buscarPedidos]);

  const abrirDetalhe = (pedido) => {
    setPedidoAberto(pedido);
    setCodigoDigitado('');
    setErroAcao('');
  };

  const fecharDetalhe = () => {
    setPedidoAberto(null);
    setErroAcao('');
  };

  const aceitarPedido = async (pedido) => {
    setProcessando(true);
    setErroAcao('');
    try {
      const response = await fetchWithAuth(`${API_URL}/entregador/pedidos/${pedido.id_pedido}/aceitar`, {
        method: 'POST'
      });
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        setErroAcao(data.error || 'Não foi possível aceitar o pedido.');
        buscarPedidos();
        return;
      }
      await buscarPedidos();
      setAba('minhas');
      fecharDetalhe();
    } catch (error) {
      setErroAcao('Erro de conexão. Tente novamente.');
    } finally {
      setProcessando(false);
    }
  };

  const liberarPedido = async (pedido) => {
    setProcessando(true);
    setErroAcao('');
    try {
      const response = await fetchWithAuth(`${API_URL}/entregador/pedidos/${pedido.id_pedido}/liberar`, {
        method: 'POST'
      });
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        setErroAcao(data.error || 'Não foi possível liberar o pedido.');
        return;
      }
      await buscarPedidos();
      fecharDetalhe();
    } catch (error) {
      setErroAcao('Erro de conexão. Tente novamente.');
    } finally {
      setProcessando(false);
    }
  };

  const marcarEntregue = async (pedido) => {
    setProcessando(true);
    setErroAcao('');
    try {
      const response = await fetchWithAuth(`${API_URL}/entregador/pedidos/${pedido.id_pedido}/entregar`, {
        method: 'POST',
        body: JSON.stringify({ codigoDigitado: codigoDigitado || undefined })
      });
      if (!response) return;
      const data = await response.json();
      if (!response.ok) {
        setErroAcao(data.error || 'Não foi possível marcar como entregue.');
        return;
      }
      await buscarPedidos();
      fecharDetalhe();
    } catch (error) {
      setErroAcao('Erro de conexão. Tente novamente.');
    } finally {
      setProcessando(false);
    }
  };

  const listaAtual = aba === 'disponiveis' ? disponiveis : minhas;

  return (
    <div className={styles.container}>
      <HeaderPageEquipe
        nomeEstabelecimento={nomeEstabelecimento}
        nomePage="Entregas"
        perfilPath="/equipe/entregador/perfil"
      />

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${aba === 'disponiveis' ? styles.tabAtiva : ''}`}
          onClick={() => setAba('disponiveis')}
        >
          Disponíveis
          {disponiveis.length > 0 && <span className={styles.tabBadge}>{disponiveis.length}</span>}
        </button>
        <button
          className={`${styles.tab} ${aba === 'minhas' ? styles.tabAtiva : ''}`}
          onClick={() => setAba('minhas')}
        >
          Minhas entregas
          {minhas.length > 0 && <span className={styles.tabBadge}>{minhas.length}</span>}
        </button>
      </div>

      <div className={styles.lista}>
        {carregando ? (
          <div className={styles.estadoVazio}>
            <p>Carregando pedidos...</p>
          </div>
        ) : listaAtual.length === 0 ? (
          <div className={styles.estadoVazio}>
            <Package size={40} className={styles.estadoVazioIcone} />
            <p>
              {aba === 'disponiveis'
                ? 'Nenhum pedido disponível para entrega no momento.'
                : 'Você ainda não aceitou nenhuma entrega.'}
            </p>
          </div>
        ) : (
          listaAtual.map((pedido) => (
            <button
              key={pedido.id_pedido}
              className={styles.cardPedido}
              onClick={() => abrirDetalhe(pedido)}
            >
              <div className={styles.cardTopo}>
                <span className={styles.cardNumero}>Pedido #{numeroExibicaoPedido(pedido)}</span>
                <span className={`${styles.statusPill} ${pedido.status === 'entregue' ? styles.statusEntregue : styles.statusPronto}`}>
                  {pedido.status === 'entregue' ? 'Entregue' : 'Pronto'}
                </span>
              </div>
              <div className={styles.cardEndereco}>
                <MapPin size={15} className={styles.cardIcone} />
                <span>{pedido.endereco_entrega || 'Endereço não informado'}</span>
              </div>
              <div className={styles.cardRodape}>
                <span className={styles.cardCliente}>{nomeCliente(pedido)}</span>
                <span className={styles.cardValor}>R$ {Number(pedido.total || 0).toFixed(2)}</span>
              </div>
            </button>
          ))
        )}
      </div>

      {pedidoAberto && (
        <div className={styles.overlay} onClick={fecharDetalhe}>
          <div className={styles.painel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.painelHeader}>
              <h3>Pedido #{numeroExibicaoPedido(pedidoAberto)}</h3>
              <button className={styles.fechar} onClick={fecharDetalhe}>
                <X size={22} />
              </button>
            </div>

            <div className={styles.painelConteudo}>
              <section className={styles.blocoEndereco}>
                <p className={styles.blocoLabel}><MapPin size={14} /> Endereço de entrega</p>
                <p className={styles.enderecoTexto}>{pedidoAberto.endereco_entrega || 'Não informado'}</p>
                <a
                  className={styles.btnMaps}
                  href={linkGoogleMaps(pedidoAberto.endereco_entrega)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Navigation size={16} /> Abrir no Google Maps
                </a>
              </section>

              <section className={styles.blocoCliente}>
                <p className={styles.blocoLabel}>Cliente</p>
                <p className={styles.clienteNome}>{nomeCliente(pedidoAberto)}</p>
                {telefoneCliente(pedidoAberto) && (
                  <a className={styles.btnTelefone} href={linkTelefone(telefoneCliente(pedidoAberto))}>
                    <Phone size={16} /> {telefoneCliente(pedidoAberto)}
                  </a>
                )}
              </section>

              <section className={styles.blocoItens}>
                <p className={styles.blocoLabel}>Itens do pedido</p>
                <ul className={styles.itensLista}>
                  {(pedidoAberto.itens || []).map((item) => (
                    <li key={item.id_item} className={styles.itemLinha}>
                      <span>{item.quantidade}x {item.produto?.nome_item || 'Item'}</span>
                      <span>R$ {Number(item.total_item || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className={styles.itemTotal}>
                  <span>Total</span>
                  <span>R$ {Number(pedidoAberto.total || 0).toFixed(2)}</span>
                </div>
              </section>

              {erroAcao && <p className={styles.mensagemErro}>{erroAcao}</p>}

              {pedidoAberto.status !== 'entregue' && (
                <section className={styles.blocoAcoes}>
                  {!pedidoAberto.entregador_id ? (
                    <button
                      className={styles.btnPrimario}
                      onClick={() => aceitarPedido(pedidoAberto)}
                      disabled={processando}
                    >
                      Aceitar entrega
                    </button>
                  ) : (
                    <>
                      <div className={styles.campoCodigo}>
                        <label htmlFor="codigoEntrega">Código de entrega (se o cliente informar)</label>
                        <input
                          id="codigoEntrega"
                          type="text"
                          maxLength={4}
                          placeholder="0000"
                          value={codigoDigitado}
                          onChange={(e) => setCodigoDigitado(e.target.value)}
                        />
                      </div>
                      <button
                        className={styles.btnPrimario}
                        onClick={() => marcarEntregue(pedidoAberto)}
                        disabled={processando}
                      >
                        Marcar como entregue
                      </button>
                      <button
                        className={styles.btnSecundario}
                        onClick={() => liberarPedido(pedidoAberto)}
                        disabled={processando}
                      >
                        Desistir da entrega
                      </button>
                    </>
                  )}
                </section>
              )}

              {pedidoAberto.status === 'entregue' && pedidoAberto.data_entrega && (
                <p className={styles.entregueEm}>
                  <Clock size={14} /> Entregue em {new Date(pedidoAberto.data_entrega).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <FooterOpcoes typeView="entregador" />
    </div>
  );
};

export default PedidosEntrega;
